import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import {
  Search, RefreshCcw, XCircle, CheckCircle2, RotateCcw,
  Calendar, DollarSign, Ban, Ticket, Clock, StickyNote,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../components/ui/hover-card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-rose-100 text-rose-700",
  completed: "bg-slate-100 text-slate-700",
  refunded: "bg-purple-100 text-purple-700",
};
const PAY_STYLES = {
  unpaid: "bg-slate-100 text-slate-600",
  paid: "bg-green-100 text-green-800",
  refunded: "bg-purple-100 text-purple-700",
  failed: "bg-rose-100 text-rose-700",
};

export default function AdminBookings() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null); // { type: 'cancel'|'refund'|'mark-paid', booking }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/bookings`, {
      params: { status: status || undefined, payment_status: payment || undefined, city: city || undefined, q: q || undefined },
    })
      .then(({ data }) => { setItems(data.items || []); setStats(data.stats || {}); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load"))
      .finally(() => setLoading(false));
  }, [status, payment, city, q]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const runAction = async () => {
    if (!action) return;
    setBusy(true);
    const path = { cancel: "cancel", refund: "refund", "mark-paid": "mark-paid" }[action.type];
    try {
      await axios.post(`${API}/admin/bookings/${action.booking.id}/${path}`, { reason: reason || null });
      toast.success(`Booking ${action.type === "mark-paid" ? "marked paid" : action.type}`);
      setAction(null); setReason(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const kpis = useMemo(() => ([
    { label: "Revenue (paid)", value: format(stats.revenue_zmw || 0), icon: DollarSign, testid: "kpi-revenue" },
    { label: "Paid bookings", value: stats.paid_count ?? 0, icon: Ticket, testid: "kpi-paid" },
    { label: "Upcoming", value: stats.upcoming_count ?? 0, icon: Clock, testid: "kpi-upcoming" },
    { label: "Cancelled", value: stats.cancelled_count ?? 0, icon: Ban, testid: "kpi-cancelled" },
    { label: "Refunded", value: stats.refunded_count ?? 0, icon: RotateCcw, testid: "kpi-refunded" },
  ]), [stats, format]);

  if (!user) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-slate-500">Please sign in.</div>;
  if (!user.is_admin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Admin only</h1>
        <p className="mt-2 text-slate-600">You don&apos;t have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8" data-testid="admin-bookings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
            <Calendar className="h-3.5 w-3.5" /> Admin · Bookings
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Bookings management</h1>
          <p className="mt-1 text-sm text-slate-600">Every booking on EasyStay — filter, search, cancel, refund, or manually mark paid.</p>
        </div>
        <button onClick={load} className="btn-outline text-sm" data-testid="bookings-refresh"><RefreshCcw className="mr-1 inline h-4 w-4" /> Refresh</button>
      </div>

      {/* KPIs */}
      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {kpis.map((k) => (
          <div key={k.label} data-testid={k.testid} className="rounded-3xl bg-white p-5 card-shadow">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
              <k.icon className="h-3.5 w-3.5 text-copper" /> {k.label}
            </div>
            <div className="mt-2 font-display text-2xl font-bold text-slate-900">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3 rounded-3xl bg-white p-4 card-shadow">
        <div className="flex flex-1 min-w-[220px] items-center gap-2 rounded-full border border-slate-200 px-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="bookings-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by guest, phone, property, ref…"
            className="w-full bg-transparent py-2 text-sm outline-none"
          />
        </div>
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
          <option value="refunded">Refunded</option>
        </select>
        <select data-testid="filter-payment" value={payment} onChange={(e) => setPayment(e.target.value)} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All payments</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="failed">Failed</option>
        </select>
        <input
          data-testid="filter-city"
          value={city} onChange={(e) => setCity(e.target.value)}
          placeholder="City (Lusaka…)"
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm"
        />
        <button onClick={load} className="btn-primary text-sm">Apply</button>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-3xl bg-white card-shadow">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading bookings…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="bookings-empty">No bookings match those filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3">Booking</th>
                <th className="px-4 py-3">Guest</th>
                <th className="px-4 py-3">Check-in</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} data-testid={`admin-booking-${b.id}`} className="border-b border-slate-50 hover:bg-sand/40">
                  <td className="px-4 py-3">
                    <Link to={`/booking/${b.id}`} className="font-semibold text-slate-900 hover:text-copper">{b.property_title}</Link>
                    <div className="text-xs text-slate-500 font-mono truncate max-w-[220px]">{b.id.slice(0, 8)}…  · {b.key_type} key</div>
                    {b.payment_ref && <div className="text-xs text-slate-400 font-mono">ref: {b.payment_ref}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 font-medium text-slate-800">
                      {b.guest_name}
                      {b.guest_has_notes && (
                        <HoverCard openDelay={80} closeDelay={80}>
                          <HoverCardTrigger asChild>
                            <button
                              type="button"
                              data-testid={`guest-note-flag-${b.id}`}
                              aria-label="This guest has private admin notes"
                              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                            >
                              !
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side="right"
                            align="start"
                            className="w-80"
                            data-testid={`guest-note-peek-${b.id}`}
                          >
                            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-rose-600">
                              <StickyNote className="h-3.5 w-3.5" /> Latest admin note
                            </div>
                            <p className="mt-2 whitespace-pre-line text-sm text-slate-800">
                              {b.guest_latest_note?.note || "(No preview available — open Guests tab.)"}
                            </p>
                            {b.guest_latest_note && (
                              <div className="mt-3 text-[11px] text-slate-500">
                                by {b.guest_latest_note.author_name} · {new Date(b.guest_latest_note.created_at).toLocaleString()}
                              </div>
                            )}
                            <Link
                              to="/admin/guests"
                              className="mt-3 inline-block text-xs font-semibold text-copper hover:underline"
                            >
                              Open guest lookup →
                            </Link>
                          </HoverCardContent>
                        </HoverCard>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{b.guest_email || "—"}</div>
                    <div className="text-xs text-slate-500 font-mono">{b.guest_phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-800">{new Date(b.check_in).toLocaleString()}</div>
                    <div className="text-xs text-slate-500">{b.duration_hours}h · {b.payment_method}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-display font-bold text-slate-900">{format(b.total_zmw)}</div>
                    <div className="text-xs text-slate-500">{b.currency_selected}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[b.status] || "bg-slate-100"}`} data-testid={`status-${b.id}`}>
                      {b.status}
                    </span>
                    <div className="mt-1">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${PAY_STYLES[b.payment_status] || "bg-slate-100"}`} data-testid={`payment-${b.id}`}>
                        {b.payment_status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      {b.payment_status !== "paid" && b.status !== "cancelled" && (
                        <button
                          onClick={() => { setAction({ type: "mark-paid", booking: b }); setReason(""); }}
                          data-testid={`mark-paid-${b.id}`}
                          className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 hover:bg-green-200"
                          title="Manually mark this booking as paid"
                        >
                          <CheckCircle2 className="mr-1 inline h-3 w-3" /> Mark paid
                        </button>
                      )}
                      {b.payment_status === "paid" && b.status !== "refunded" && (
                        <button
                          onClick={() => { setAction({ type: "refund", booking: b }); setReason(""); }}
                          data-testid={`refund-${b.id}`}
                          className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800 hover:bg-purple-200"
                          title="Mark as refunded (finance settles off-platform)"
                        >
                          <RotateCcw className="mr-1 inline h-3 w-3" /> Refund
                        </button>
                      )}
                      {b.status !== "cancelled" && (
                        <button
                          onClick={() => { setAction({ type: "cancel", booking: b }); setReason(""); }}
                          data-testid={`cancel-${b.id}`}
                          className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-200"
                          title="Cancel this booking"
                        >
                          <Ban className="mr-1 inline h-3 w-3" /> Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Action confirmation dialog */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setReason(""); } }}>
        <DialogContent data-testid="booking-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action?.type === "cancel" ? "Cancel booking" : action?.type === "refund" ? "Refund booking" : "Mark booking as paid"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl bg-slate-50 p-3 text-sm">
              <div className="font-semibold text-slate-900">{action?.booking?.property_title}</div>
              <div className="text-xs text-slate-500">{action?.booking?.guest_name} · {format(action?.booking?.total_zmw ?? 0)}</div>
            </div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Reason (visible to the guest)</label>
            <textarea
              data-testid="action-reason"
              value={reason} onChange={(e) => setReason(e.target.value.slice(0, 400))}
              rows={3}
              placeholder={action?.type === "mark-paid" ? "e.g. USSD receipt #12345 confirmed" : "e.g. Guest requested cancellation"}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setAction(null)} className="btn-outline text-sm">Never mind</button>
            <button onClick={runAction} disabled={busy} data-testid="action-confirm" className="btn-primary text-sm">
              {busy ? "Working…" : "Confirm"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
